import { defineStore } from "pinia";
import { useChannelStore } from "./channel.store";
import { computed, reactive, watchEffect, ref } from "vue";
import { graphql as gql } from "@/generated";
import { apolloClient } from "@/utils/apollo";
import type { BrandTheme } from "@/generated/graphql";
import { getDefaultDomainChannel } from "@/utils/channel";
import type { CustomGoogleFont } from "@/composables/ui/use-google-font";

const themeStoreLogPrefix = "[ThemeStore] ";

export enum AppThemeKind {
  channel = "channel",
  community = "community",
}

export interface AppTheme {
  kind: AppThemeKind;
  id: string;
}

// Default brand theme to use when no theme is loaded yet
const DEFAULT_BRAND_THEME: BrandTheme = {
  id: "default",
  updated_at: new Date().toISOString(),
  created_at: new Date().toISOString(),
  banner: null,
  brand_bg_main: "hsla(0, 0%, 8%, 1)", //"#100b1e",
  brand_strong_bg_overlay_80: "#100b1e80",
  brand_light_bg_overlay_50: "#100b1ecc",
  brand_primary: "#875af9",
  brand_surface_hover: "#875af94d",
  brand_bgdivider_10: "#875af933",
  brand_bgdivider_20: "#875af91a",
  brand_quaternary_40: "#875af966",
  brand_secondary_80: "#875af9cc",
  brand_tertiary_60: "#875af999",
  brand_fill_dark: "#00000099",
  brand_icon_emphasis: "#ffffff",
  bg: "#100B1E",
  bg_secondary: "#100B1EB3",
  surface: "#875AF91A",
  surface_hover: "#875AF933",
  surface_secondary: "#FFFFFF0D",
  surface_secondary_hover: "#FFFFFF1A",
  surface_accent: "#FF8D3A",
  fill_brand: "#875AF9",
  fill_brand_hover: "#875AF9CC",
  fill_brand_pressed: "#875AF999",
  fill_brand_secondary: "#875AF94D",
  fill_brand_secondary_hover: "#875AF980",
  fill_brand_secondary_pressed: "#875AF980",
  fill_dark: "#00000099",
  fill_dark_hover: "#00000066",
  fill_dark_pressed: "#000000CC",
  fill_disabled: "#7E7E7E66",
  fill_success: "#0DD32D",
  fill_critical: "#ED3024",
  fill_critical_secondary: "#ED30244D",
  fill_warning: "#FF8D3A",
  fill_warning_secondary: "#FF8D3A4D",
  fill_silver: "#7E7E7E",
  fill_bronze: "#AB8F54",
  fill_gold: "#FFCA17",
  fill_gold_light: "#EFEDDB",
  fill_gold_dark: "#977400",
  text: "#FFFFFF",
  text_on_brand: "#FFFFFF",
  text_inverse: "#000000",
  text_secondary: "#FFFFFFCC",
  text_tertiary: "#FFFFFF99",
  text_disabled: "#FFFFFF66",
  text_success: "#0DD32D",
  text_critical: "#ED3024",
  text_brand: "#875AF9",
  text_accent: "#BC5BAA",
  text_accent_secondary: "#F15C5C",
  text_gold: "#FFCA17",
  text_gold_light: "#EFEDDB",
  text_gold_dark: "#977400",
  border: "#FFFFFF0D",
  border_hover: "#FFFFFF1A",
  border_pressed: "#FFFFFF4D",
  border_selected: "#FFFFFF66",
  border_secondary: "#FFFFFF1A",
  border_brand: "#875AF9",
  border_brand_hover: "#875AF9CC",
  border_brand_pressed: "#875AF999",
  border_brand_selected: "#875AF9B3",
  border_critical: "#ED3024",
  border_success: "#0DD32D",
  border_warning: "#FF8D3A",
  border_dark: "#100B1E",
  border_button_shadow_light: "#FFFFFF66",
  border_button_shadow_dark: "#00000066",
  icon: "#FFFFFF80",
  icon_emphasis: "#FFFFFF",
  icon_on_branded_surface: "#FFFFFF",
  icon_hover: "#FFFFFFB3",
  icon_secondary: "#FFFFFF66",
  icon_secondary_hover: "#FFFFFF80",
  icon_disabled: "#FFFFFF66",
  icon_brand: "#875AF9",
  __typename: "BrandTheme",
} as BrandTheme;

const BrandThemeDocument = gql(/* GraphQL */ `
  query BrandTheme($channel_id: String, $community_id: String) {
    getBrandTheme(channel_id: $channel_id, community_id: $community_id) {
      ... on BrandTheme {
        id
        updated_at
        created_at

        banner

        brand_bg_main
        brand_strong_bg_overlay_80
        brand_light_bg_overlay_50
        brand_primary
        brand_surface_hover
        brand_bgdivider_10
        brand_bgdivider_20
        brand_quaternary_40
        brand_secondary_80
        brand_tertiary_60
        brand_fill_dark
        brand_icon_emphasis

        bg
        bg_secondary
        surface
        surface_hover
        surface_secondary
        surface_secondary_hover
        surface_accent
        fill_brand
        fill_brand_hover
        fill_brand_pressed
        fill_brand_secondary
        fill_brand_secondary_hover
        fill_brand_secondary_pressed
        fill_dark
        fill_dark_hover
        fill_dark_pressed
        fill_disabled
        fill_success
        fill_critical
        fill_critical_secondary
        fill_warning
        fill_warning_secondary
        fill_silver
        fill_bronze
        fill_gold
        fill_gold_light
        fill_gold_dark
        text
        text_on_brand
        text_inverse
        text_secondary
        text_tertiary
        text_disabled
        text_success
        text_critical
        text_brand
        text_accent
        text_accent_secondary
        text_gold
        text_gold_light
        text_gold_dark
        border
        border_hover
        border_pressed
        border_selected
        border_secondary
        border_brand
        border_brand_hover
        border_brand_pressed
        border_brand_selected
        border_critical
        border_success
        border_warning
        border_dark
        border_button_shadow_light
        border_button_shadow_dark
        icon
        icon_emphasis
        icon_on_branded_surface
        icon_hover
        icon_secondary
        icon_secondary_hover
        icon_disabled
        icon_brand
      }
      ... on NotFoundError {
        error
      }
    }
  }
`);

enum FetchAppThemeResultKind {
  Ok = "ok",
  NotFound = "not_found",
  Error = "error",
}

type FetchAppThemeResult =
  | { kind: FetchAppThemeResultKind.Ok; theme: BrandTheme }
  | { kind: FetchAppThemeResultKind.NotFound }
  | { kind: FetchAppThemeResultKind.Error; error: unknown };

const MAX_THEME_FETCH_ATTEMPTS = 3;
const THEME_FETCH_INITIAL_DELAY_MS = 500;
// After all inline retries fail, block auto-refetch for this long. A timer
// clears the entry once the window expires so the reactive watcher naturally
// re-attempts without needing a user-driven state change.
const THEME_FETCH_FAILURE_COOLDOWN_MS = 30_000;

async function fetchAppThemeOnce(
  theme: AppTheme,
): Promise<FetchAppThemeResult> {
  try {
    const response = await apolloClient.query({
      query: BrandThemeDocument,
      variables:
        theme.kind === AppThemeKind.channel
          ? { channel_id: theme.id }
          : { community_id: theme.id },
    });

    const result = response.data?.getBrandTheme;

    // Unexpected shape — treat as transient error so the retry loop kicks in
    // rather than blacklisting a theme that may actually exist.
    if (!result) {
      return {
        kind: FetchAppThemeResultKind.Error,
        error: new Error("getBrandTheme returned null"),
      };
    }

    if ("error" in result) {
      console.warn(
        themeStoreLogPrefix +
          `Brand theme not found for ${theme.kind} with id ${theme.id}`,
      );
      return { kind: FetchAppThemeResultKind.NotFound };
    }

    return { kind: FetchAppThemeResultKind.Ok, theme: result };
  } catch (error) {
    return { kind: FetchAppThemeResultKind.Error, error };
  }
}

async function fetchAppTheme(theme: AppTheme): Promise<FetchAppThemeResult> {
  let lastResult: FetchAppThemeResult | null = null;

  for (let attempt = 1; attempt <= MAX_THEME_FETCH_ATTEMPTS; attempt++) {
    const result = await fetchAppThemeOnce(theme);

    // Only transient errors are retried — a genuine NotFoundError is terminal.
    if (result.kind !== FetchAppThemeResultKind.Error) return result;

    lastResult = result;

    if (attempt < MAX_THEME_FETCH_ATTEMPTS) {
      const delay = THEME_FETCH_INITIAL_DELAY_MS * 2 ** (attempt - 1);
      console.warn(
        themeStoreLogPrefix +
          `Theme fetch for ${theme.kind}/${theme.id} failed (attempt ${attempt}/${MAX_THEME_FETCH_ATTEMPTS}); retrying in ${delay}ms`,
        result.error,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  console.error(
    themeStoreLogPrefix +
      `Theme fetch for ${theme.kind}/${theme.id} failed after ${MAX_THEME_FETCH_ATTEMPTS} attempts`,
    lastResult?.kind === FetchAppThemeResultKind.Error
      ? lastResult.error
      : undefined,
  );
  return lastResult!;
}

export const useThemeStore = defineStore("theme", () => {
  const channelStore = useChannelStore();

  // Used to set theme in show page
  const overrideTheme = ref<AppTheme | null>(null);

  const currentTheme = computed<AppTheme | null>(() => {
    if (overrideTheme.value) {
      return overrideTheme.value;
    }

    if (channelStore.selectedCommunity) {
      return {
        kind: AppThemeKind.community,
        id: channelStore.selectedCommunity,
      };
    }

    if (channelStore.channelSlug) {
      return {
        kind: AppThemeKind.channel,
        id: channelStore.channelSlug,
      };
    }

    return null;
  });

  const defaultTheme = computed((): AppTheme | null => {
    const domain = window.location.host;
    const defaultAppChannel = getDefaultDomainChannel(domain);

    return {
      kind: AppThemeKind.channel,
      id: channelStore.channelSlug ?? defaultAppChannel,
    };
  });

  const themeDataMap = reactive(new Map<string, BrandTheme>());
  const loadingThemeDataMap = reactive(new Map<string, boolean>());
  const notFoundThemeDataMap = reactive(new Map<string, boolean>());
  // Tracks themes whose retry loop has exhausted all attempts. Entries are
  // removed automatically after THEME_FETCH_FAILURE_COOLDOWN_MS, triggering
  // the reactive watcher to re-attempt.
  const failedFetchAtMap = reactive(new Map<string, number>());

  function isInFailureCooldown(theme: AppTheme): boolean {
    const lastFailedAt = failedFetchAtMap.get(theme.id);
    if (lastFailedAt === undefined) return false;
    return Date.now() - lastFailedAt < THEME_FETCH_FAILURE_COOLDOWN_MS;
  }

  const currentThemeData = computed(() => {
    const selectedThemeData =
      currentTheme.value === null
        ? null
        : (themeDataMap.get(currentTheme.value.id) ?? null);

    // If the selected theme is not available in the map, try default theme
    if (!selectedThemeData && !!defaultTheme.value) {
      const defaultThemeData = themeDataMap.get(defaultTheme.value.id) ?? null;
      if (defaultThemeData) {
        return defaultThemeData;
      }
    }

    // Always return theme data - use default theme if nothing else is available
    return selectedThemeData ?? DEFAULT_BRAND_THEME;
  });

  async function refetchTheme() {
    const theme = currentTheme.value;
    if (!theme) {
      console.warn(
        themeStoreLogPrefix + "refetchTheme called with no current theme",
      );
      return;
    }
    // Manual refetch expresses user intent to retry — clear every guard so
    // we don't silently no-op on a prior NotFound or failure-cooldown state.
    notFoundThemeDataMap.delete(theme.id);
    failedFetchAtMap.delete(theme.id);
    loadingThemeDataMap.delete(theme.id);
    await fetchTheme(theme);
  }

  async function fetchTheme(theme: AppTheme): Promise<boolean> {
    if (loadingThemeDataMap.get(theme.id)) {
      console.log(
        themeStoreLogPrefix +
          `Skipping theme update for ${theme.id} since it's already loading`,
      );
      return false;
    }

    if (notFoundThemeDataMap.get(theme.id)) {
      console.warn(
        themeStoreLogPrefix +
          `Skipping theme fetch for ${theme.kind}/${theme.id}: previously marked not-found`,
      );
      return false;
    }

    if (isInFailureCooldown(theme)) {
      console.log(
        themeStoreLogPrefix +
          `Skipping theme fetch for ${theme.kind}/${theme.id}: in failure cooldown`,
      );
      return false;
    }

    // Set loading before any await so concurrent callers dedupe correctly.
    loadingThemeDataMap.set(theme.id, true);

    let result: FetchAppThemeResult;
    try {
      result = await fetchAppTheme(theme);
    } catch (error) {
      // fetchAppTheme should never throw — its inner calls are wrapped in
      // try/catch — but defend against unexpected programmer errors so we
      // never leak a permanently-locked loading slot.
      console.error(
        themeStoreLogPrefix +
          `Unexpected throw while fetching ${theme.kind}/${theme.id}`,
        error,
      );
      result = { kind: FetchAppThemeResultKind.Error, error };
    } finally {
      loadingThemeDataMap.set(theme.id, false);
    }

    switch (result.kind) {
      case FetchAppThemeResultKind.NotFound:
        // Genuine NotFoundError from the server — the theme doesn't exist.
        // Blacklist to stop further fetches for this session. refetchTheme()
        // clears this if the user explicitly retries.
        notFoundThemeDataMap.set(theme.id, true);
        console.error(
          themeStoreLogPrefix +
            `Theme not found for ${theme.id}. Falling back to default.`,
        );
        return false;
      case FetchAppThemeResultKind.Error: {
        // Transient network/server error after exhausting inline retries.
        // Record the failure timestamp so the reactive watcher won't bounce
        // into a tight re-fetch loop, and schedule a clear so we naturally
        // re-attempt once the cooldown expires.
        failedFetchAtMap.set(theme.id, Date.now());
        setTimeout(() => {
          failedFetchAtMap.delete(theme.id);
        }, THEME_FETCH_FAILURE_COOLDOWN_MS);
        return false;
      }
      case FetchAppThemeResultKind.Ok:
        themeDataMap.set(theme.id, result.theme);
        return true;
    }
  }

  async function toggleSelectedCommunityTheme(communityId: string) {
    if (communityId === currentTheme.value?.id) {
      channelStore.toggleSelectedCommunity(communityId);
    } else {
      const success = await fetchTheme({
        kind: AppThemeKind.community,
        id: communityId,
      });

      if (success) {
        channelStore.toggleSelectedCommunity(communityId);
      }
    }
  }

  // Single source of truth for "should the auto-fetching watcher skip this
  // theme right now?". Read inside watchEffects so every condition here is
  // a reactive dependency — when any map entry changes, the watcher re-runs.
  function shouldSkipFetch(theme: AppTheme): boolean {
    return (
      themeDataMap.has(theme.id) ||
      !!loadingThemeDataMap.get(theme.id) ||
      notFoundThemeDataMap.has(theme.id) ||
      isInFailureCooldown(theme)
    );
  }

  const customSerifFont = computed<CustomGoogleFont | null>(() => {
    if (channelStore.isAsMonacoFanToken) {
      return { family: "Montserrat", weights: [500] };
    }

    return null;
  });

  watchEffect(() => {
    // Fetch the current (or override) theme when it's missing and eligible.
    const anyTheme = currentTheme.value ?? defaultTheme.value;
    if (!anyTheme) return;
    if (shouldSkipFetch(anyTheme)) return;

    console.log(
      themeStoreLogPrefix +
        `Fetching theme data for ${anyTheme.kind}/${anyTheme.id}`,
    );
    fetchTheme(anyTheme);
  });

  watchEffect(() => {
    // Ensure the channel-level default theme is always cached so
    // currentThemeData has a proper fallback while the selected theme loads.
    const theme = defaultTheme.value;
    if (!theme) return;
    if (shouldSkipFetch(theme)) return;

    console.log(
      themeStoreLogPrefix +
        `Fetching default theme data for ${theme.kind}/${theme.id}`,
    );
    fetchTheme(theme);
  });

  // If the user un-follows the (selected) community (theme), reset the theme to the default
  watchEffect(() => {
    const currentThemeValue = currentTheme.value;

    if (
      currentThemeValue?.kind === AppThemeKind.community &&
      channelStore.channelCommunities.length > 0
    ) {
      const selectedCommunity = channelStore.channelCommunities.find(
        (c) => c.id === currentThemeValue.id,
      );

      if (!selectedCommunity) channelStore.resetCommunitySelection();
    }
  });

  return {
    overrideTheme,
    currentTheme,
    customSerifFont,
    currentThemeData,
    loadingThemeDataMap,
    toggleSelectedCommunityTheme,
    loading: computed(() => false),
    refetchTheme,
  };
});
